import { ConfiguracaoEscolaCodigo } from '@/enums/configuracoes-escola.enum';
import gesParametrosService from '@/services/gesparametros/gesparametros.service';
import { useStore } from '@/store';
import { reactive } from 'vue';

/**
 * Nomenclatura do PDI configurável por cliente (parâmetros GesParametros
 * `pdiSigla` / `pdiDescricao`, códigos 56/57 do enum ConfiguracaoEscolaCodigo).
 * Diferentes legislações municipais/estaduais dão outro nome ao PDI; por padrão a
 * sigla é "PDI" e a descrição "Plano de Desenvolvimento Individual".
 *
 * O estado é reativo (para o menu/breadcrumb e as telas do módulo reagirem) e
 * persistido em localStorage (para já estar disponível de forma síncrona no
 * próximo carregamento, sem "piscar" o valor padrão).
 */

export const SIGLA_PADRAO = 'PDI';
export const DESCRICAO_PADRAO = 'Plano de Desenvolvimento Individual';

const STORAGE_KEY = 'pdiNomenclatura';

interface EstadoNomenclatura {
  sigla: string;
  descricao: string;
  chave: string;
}

const estado = reactive<EstadoNomenclatura>({
  sigla: SIGLA_PADRAO,
  descricao: DESCRICAO_PADRAO,
  chave: '',
});

function hidratarDoStorage(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const persistido = JSON.parse(raw) as Partial<EstadoNomenclatura>;
    if (persistido?.sigla) estado.sigla = persistido.sigla;
    if (persistido?.descricao) estado.descricao = persistido.descricao;
  } catch {
    /* localStorage indisponível/corrompido — mantém o padrão */
  }
}
hidratarDoStorage();

class PdiNomenclaturaService {
  get sigla(): string {
    return estado.sigla || SIGLA_PADRAO;
  }

  get descricao(): string {
    return estado.descricao || DESCRICAO_PADRAO;
  }

  /**
   * Carrega a nomenclatura para a escola/ano informados. Faz cache pela chave
   * escola+ano — chamadas repetidas para o mesmo contexto não batem na API.
   */
  async carregar(codigoEscola: number, ano: number): Promise<void> {
    if (!codigoEscola || !ano) return;

    const chave = `${codigoEscola}-${ano}`;
    if (estado.chave === chave) return;

    try {
      const [sigla, descricao] = await Promise.all([
        this.buscarValor(ConfiguracaoEscolaCodigo.pdiSigla, ano, codigoEscola, SIGLA_PADRAO),
        this.buscarValor(ConfiguracaoEscolaCodigo.pdiDescricao, ano, codigoEscola, DESCRICAO_PADRAO),
      ]);

      estado.sigla = sigla;
      estado.descricao = descricao;
      // Só marca o contexto como resolvido APÓS o sucesso: assim uma falha transitória não
      // fica cacheada como padrão (seria re-tentada na próxima chamada), e a chave sempre
      // acompanha os valores que acabaram de ser aplicados.
      estado.chave = chave;
      this.persistir();
    } catch {
      // Falha ao ler os parâmetros nunca deve quebrar a UI — mantém o estado atual e NÃO
      // marca a chave, para re-tentar quando a API voltar.
    }
  }

  /**
   * Grava a nomenclatura (sigla/descrição) para a escola/ano **selecionados no store** (topo do
   * sistema) e reflete de imediato no estado reativo. O ano vem do store (`getAno`) de propósito: o
   * PK local de cada parâmetro é resolvido para exatamente esse ano — assim a gravação acerta a linha
   * do ano selecionado (o `ObterPorCodPar` faz `COALESCE(ano, ano-1)`, então o ano tem de ser o
   * correto). Vazio cai no padrão (grava o valor padrão na linha local da escola/ano).
   */
  async atualizar(sigla: string, descricao: string): Promise<void> {
    const store = useStore();
    const codigoEscola = Number(store.getters.getCodigoEscola ?? 0);
    const ano = Number(store.getters.getAno ?? 0);
    if (!codigoEscola || !ano) return;

    const novaSigla = (sigla ?? '').trim() || SIGLA_PADRAO;
    const novaDescricao = (descricao ?? '').trim() || DESCRICAO_PADRAO;

    // Resolve o PK local (GESPARAMETROS.CODIGO) de cada parâmetro para o ano selecionado no store e
    // grava pelo AtualizarValor existente (PUT /GesParametros por PK, escopado por ano).
    const [paramSigla, paramDescricao] = await Promise.all([
      gesParametrosService.getParametro(ConfiguracaoEscolaCodigo.pdiSigla, codigoEscola, ano),
      gesParametrosService.getParametro(ConfiguracaoEscolaCodigo.pdiDescricao, codigoEscola, ano),
    ]);

    await Promise.all([
      gesParametrosService.inserirAtualizar(paramSigla.codigo, novaSigla),
      gesParametrosService.inserirAtualizar(paramDescricao.codigo, novaDescricao),
    ]);

    estado.sigla = novaSigla;
    estado.descricao = novaDescricao;
    estado.chave = `${codigoEscola}-${ano}`;
    this.persistir();
  }

  /**
   * Lê a nomenclatura da escola/ano **selecionados no store** — para preencher os campos editáveis do
   * modal do suporte, casando com o alvo do `atualizar` (que também grava no ano do store). Ao contrário
   * do `carregar`, NÃO toca o estado reativo global (que segue o contexto visualizado / labels do menu);
   * apenas retorna os valores. Vazio/indisponível cai no padrão.
   */
  async obterParaEdicao(): Promise<{ sigla: string; descricao: string }> {
    const store = useStore();
    const codigoEscola = Number(store.getters.getCodigoEscola ?? 0);
    const ano = Number(store.getters.getAno ?? 0);
    if (!codigoEscola || !ano) {
      return { sigla: this.sigla, descricao: this.descricao };
    }

    const [sigla, descricao] = await Promise.all([
      this.buscarValor(ConfiguracaoEscolaCodigo.pdiSigla, ano, codigoEscola, SIGLA_PADRAO),
      this.buscarValor(ConfiguracaoEscolaCodigo.pdiDescricao, ano, codigoEscola, DESCRICAO_PADRAO),
    ]);

    return { sigla, descricao };
  }

  /** Substitui os tokens `{pdiSigla}` / `{pdiDescricao}` num texto. */
  aplicarTokens(texto: string | undefined | null): string {
    if (!texto) return texto ?? '';
    return texto
      .replace(/\{pdiSigla\}/g, this.sigla)
      .replace(/\{pdiDescricao\}/g, this.descricao);
  }

  private async buscarValor(
    codigo: ConfiguracaoEscolaCodigo,
    ano: number,
    codigoEscola: number,
    padrao: string,
  ): Promise<string> {
    // Uma falha de rede é propagada de propósito para o carregar() não cachear o padrão como
    // se fosse sucesso; valor vazio (parâmetro sem override) cai no padrão normalmente.
    const parametro = await gesParametrosService.getParametro(codigo, codigoEscola, ano);
    const valor = parametro?.valorParametro?.trim();
    return valor ? valor : padrao;
  }

  private persistir(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ sigla: estado.sigla, descricao: estado.descricao }),
      );
    } catch {
      /* localStorage indisponível — segue só com o estado em memória */
    }
  }
}

export default new PdiNomenclaturaService();
